import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Package, Plus, Minus, X, FilePdf, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { isDeadPdf, pdfLabel, pdfOriginal, stripDeadLinks } from "@/lib/pdfUtil";

const nm = (o, lang) => (o && (o[lang] || o.fr)) || "";

export function Thumb({ src, className }) {
  const [err, setErr] = useState(false);
  return (
    <div className={`flex items-center justify-center overflow-hidden bg-white ${className}`}>
      {src && !err ? <img loading="lazy" decoding="async" src={src} alt="" className="h-full w-full object-contain" onError={() => setErr(true)} /> : <Package size={28} className="text-muted-foreground" />}
    </div>
  );
}

function CategoryRow({ cats, value, onChange, lang, allLabel, testPrefix }) {
  return (
    <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-2" style={{ scrollbarWidth: "none" }}>
      {allLabel && (
        <button data-testid={`${testPrefix}-all`} onClick={() => onChange("")}
          className={`shrink-0 rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${!value ? "border-primary bg-primary text-white" : "border-border bg-white text-[#2e3a47]"}`}>{allLabel}</button>
      )}
      {cats.map((c) => (
        <button key={c.category_id} data-testid={`${testPrefix}-${c.category_id}`} onClick={() => onChange(c.category_id)}
          className={`shrink-0 whitespace-nowrap rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${value === c.category_id ? "border-primary bg-primary text-white" : "border-border bg-white text-[#2e3a47]"}`}>{nm(c.name, lang)}</button>
      ))}
    </div>
  );
}

export function VendorCatalog({ L, lang, items, onAdd }) {
  const [cats, setCats] = useState([]);
  const [root, setRoot] = useState("");
  const [sub, setSub] = useState("");
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const onPop = () => setOpenId(null);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const open = (pid) => { window.history.pushState({ vendorSheet: pid }, ""); setOpenId(pid); };
  const close = () => window.history.back();
  useEffect(() => { axios.get(`${API}/shop/categories`).then((r) => setCats(r.data || [])).catch(() => {}); }, []);
  const category = sub || root;
  useEffect(() => {
    let live = true;
    setLoading(true);
    axios.get(`${API}/vendor/catalog`, { params: { category: category || undefined, page } })
      .then((r) => { if (!live) return; setTotal(r.data.total); setList((prev) => (page === 1 ? r.data.items : [...prev, ...r.data.items])); })
      .catch(() => {}).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [category, page]);

  const pickRoot = (id) => { setRoot(id); setSub(""); setPage(1); };
  const pickSub = (id) => { setSub(id); setPage(1); };
  const children = (cats.find((c) => c.category_id === root)?.children) || [];
  const inCart = (pid) => items.some((it) => it.product_id === pid);

  return (
    <div data-testid="vendor-catalog" className="mt-5">
      <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{L.catalog}</div>
      <CategoryRow cats={cats} value={root} onChange={pickRoot} lang={lang} allLabel={L.all} testPrefix="vendor-cat" />
      {children.length > 0 && <CategoryRow cats={children} value={sub} onChange={(id) => pickSub(id === sub ? "" : id)} lang={lang} testPrefix="vendor-subcat" />}
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {list.map((p) => (
          <div key={p.product_id} data-testid={`vendor-catalog-card-${p.product_id}`} className="relative flex flex-col overflow-hidden rounded-lg border border-border bg-white shadow-sm">
            <button data-testid={`vendor-catalog-open-${p.product_id}`} onClick={() => open(p.product_id)} className="flex flex-1 flex-col text-left active:opacity-80">
              <Thumb src={p.image} className="aspect-square w-full border-b border-border p-2" />
              <div className="flex-1 p-2.5">
                <div className="line-clamp-2 text-[13px] font-medium leading-snug text-[#2e3a47]">{nm(p.name, lang)}</div>
                <div className="mt-1 font-mono text-[10px] text-primary">{p.variation_group_id ? L.severalFormats : p.product_code}</div>
              </div>
            </button>
            <button data-testid={`vendor-catalog-add-${p.product_id}`} aria-label="add"
              onClick={() => (p.variation_group_id ? open(p.product_id) : onAdd({ ...p, variant_value: "" }, 1))}
              className={`absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full shadow-md transition-transform active:scale-90 ${inCart(p.product_id) ? "bg-emerald-500 text-white" : "bg-primary text-white"}`}>
              {inCart(p.product_id) ? <CheckCircle size={20} weight="bold" /> : <Plus size={20} weight="bold" />}
            </button>
          </div>
        ))}
      </div>
      {!loading && list.length === 0 && <div className="mt-3 rounded-lg border border-dashed border-border bg-white p-6 text-center text-sm text-muted-foreground">{L.noCatalog}</div>}
      {loading && <div className="mt-3 text-center text-sm text-muted-foreground">…</div>}
      {!loading && list.length < total && (
        <button data-testid="vendor-catalog-more" onClick={() => setPage((x) => x + 1)} className="mt-4 w-full rounded-lg border border-border bg-white py-3 text-sm font-bold uppercase tracking-wider text-primary">{L.more} ({list.length}/{total})</button>
      )}
      {openId && <ProductSheet id={openId} cartCount={items.length} L={L} lang={lang} onClose={close} onAdd={(p, q) => { onAdd(p, q); close(); }} />}
    </div>
  );
}

function sheetsOf(p, lang) {
  const byKey = new Map();
  for (const src of [p, ...(p.variants || [])]) {
    const arr = ((lang === "en" && src.pdfs_en?.length) ? src.pdfs_en : src.pdfs_fr) || [];
    for (const e of arr) {
      if (!e.url || isDeadPdf(e.url)) continue;
      const key = pdfOriginal(e.url).split("?")[0].split("/").pop();
      if (!byKey.has(key)) byKey.set(key, { url: e.url, label: lang === "en" ? (e.name_en || e.name_fr) : (e.name_fr || e.name_en) });
    }
  }
  return [...byKey.values()].map((e, i) => ({ url: e.url, label: e.label || pdfLabel(e.url, i, lang) }));
}

function ProductSheet({ id, L, lang, onClose, onAdd, cartCount }) {
  const [p, setP] = useState(null);
  const [sel, setSel] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    axios.get(`${API}/vendor/catalog/${id}`).then((r) => {
      setP(r.data);
      const vs = r.data.variants || [];
      setSel(vs.length ? (vs.find((v) => v.product_id === r.data.product_id) || vs[0]).product_id : r.data.product_id);
    }).catch(() => onClose());
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const variants = p?.variants || [];
  const cur = variants.find((v) => v.product_id === sel) || p;
  const label = p && (lang === "en" ? (p.variation_label_en || p.variation_label) : p.variation_label);

  return (
    <div data-testid="vendor-product-sheet" className="fixed inset-0 z-50 flex flex-col bg-white">
      <div className="flex items-center gap-3 border-b border-border bg-[#2e3a47] px-3 py-2.5 text-white">
        <button data-testid="vendor-sheet-back" onClick={onClose}
          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-sm font-bold uppercase tracking-wider text-[#2e3a47] shadow active:scale-95">
          <ArrowLeft size={18} weight="bold" /> {L.backToCatalog}
        </button>
        <span className="min-w-0 flex-1 truncate text-xs font-bold uppercase tracking-wider opacity-80">{p ? nm(p.name, lang) : "…"}</span>
        <button data-testid="vendor-sheet-close" onClick={onClose} className="shrink-0 rounded p-1 hover:bg-white/10"><X size={22} weight="bold" /></button>
      </div>
      {!p && <div data-testid="vendor-sheet-loading" className="flex flex-1 items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}
      {p && (
        <>
          <div className="flex-1 overflow-y-auto overflow-x-hidden break-words px-4 py-4 [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5">
            <Thumb src={cur?.image || p.image} className="mx-auto aspect-square w-full max-w-sm rounded-lg border border-border p-3" />
            <h2 className="mt-4 text-lg font-bold leading-snug text-[#2e3a47]">{nm(cur?.name, lang) || nm(p.name, lang)}</h2>
            <div data-testid="vendor-sheet-code" className="mt-1 font-mono text-xs text-primary">{cur?.product_code}</div>
            {variants.length > 0 && (
              <div className="mt-4">
                <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label || L.variation}</div>
                <div className="flex flex-wrap gap-2">
                  {variants.map((v) => (
                    <button key={v.product_id} data-testid={`vendor-variant-${v.product_id}`} onClick={() => setSel(v.product_id)}
                      className={`rounded-lg border px-3 py-2 text-sm font-bold transition-colors ${sel === v.product_id ? "border-primary bg-primary text-white" : "border-border bg-white text-[#2e3a47]"}`}>{v.value || v.product_code}</button>
                  ))}
                </div>
              </div>
            )}
            {nm(p.description, lang) && (
              <div data-testid="vendor-sheet-description" className="prose prose-sm mt-5 max-w-none text-sm text-[#2e3a47] [&_img]:max-w-full"
                dangerouslySetInnerHTML={{ __html: stripDeadLinks(nm(p.description, lang)) }} />
            )}
            {sheetsOf(p, lang).length > 0 && (
              <div className="mt-5 space-y-2" data-testid="vendor-sheet-pdfs">
                {sheetsOf(p, lang).map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noreferrer" data-testid={`vendor-sheet-pdf-${i}`}
                    className="flex items-center gap-2 rounded-lg border border-border p-3 text-sm font-medium text-[#2e3a47] active:bg-muted">
                    <FilePdf size={18} weight="bold" className="shrink-0 text-primary" /> {s.label}
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="border-t border-border bg-white px-4 pt-3">
            <button data-testid="vendor-sheet-back-bottom" onClick={onClose}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-white py-2.5 text-sm font-bold uppercase tracking-wider text-[#2e3a47] active:scale-95">
              <ArrowLeft size={16} weight="bold" /> {L.backToCatalog} {cartCount > 0 ? `· ${L.cartLabel} (${cartCount})` : ""}
            </button>
          </div>
          <div className="flex items-center gap-3 bg-white px-4 py-3">
            <div className="flex items-center gap-1">
              <button data-testid="vendor-sheet-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-11 w-11 items-center justify-center rounded-lg border border-border"><Minus size={16} weight="bold" /></button>
              <input data-testid="vendor-sheet-qty" type="number" step="0.01" value={qty} onChange={(e) => setQty(parseFloat(e.target.value) || 1)} className="h-11 w-16 rounded-lg border border-border text-center text-base outline-none focus:border-primary" />
              <button data-testid="vendor-sheet-qty-plus" onClick={() => setQty((q) => q + 1)} className="flex h-11 w-11 items-center justify-center rounded-lg border border-border"><Plus size={16} weight="bold" /></button>
            </div>
            <button data-testid="vendor-sheet-add" onClick={() => onAdd({ product_id: cur.product_id, product_code: cur.product_code, image: cur.image || p.image || "", name: cur.name || p.name, variant_value: cur.value || p.variant_value || "", variation_label: p.variation_label || "" }, qty)}
              className="flex h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-primary text-sm font-bold uppercase tracking-wider text-white shadow-lg active:scale-95">
              <Plus size={16} weight="bold" /> {L.add}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
